/* eslint-disable react-hooks/exhaustive-deps */
/** @jsxImportSource @emotion/react */
import tw from 'twin.macro';
import { css } from '@emotion/react';

import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useToggle } from 'react-use';
import { isEmpty, isNaN } from 'lodash';
import { v4 } from 'uuid';

import { ReactComponent as ErrorIcon } from 'assets/Icons/error_outline_black_24dp.svg';
import { Modal } from 'components/UiControls/modal/modal';
import { Loader } from 'components/loader/loader';
import { LibraryService, UploadService } from 'services';
import { getLatestTitle } from 'components/VideoPlayer/Transcription/MediaUtilities';
import {
  BakedInAdvancedOptions,
  BakedInConfig,
  BakedInDetectionMethod,
  BakedInDetectionMethods,
  BakedInTextDetector,
  IUploadHeaders,
  ILibraryItem,
} from 'utils/models';
import { ButtonGroup } from '../ButtonGroup';
import { Switcher } from '../Switcher';

type Props = {
  show: boolean;
  collectionId: string;
  config: BakedInConfig;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (advanced: BakedInAdvancedOptions) => void;
};

const MAX_SPONSOR_BRAND_LENGTH = 64;
const MAX_SPONSOR_BRAND_COUNT = 10;

export const JINGLE_ALLOWED_EXTENSIONS = ['mp3', 'wav'];
export const JINGLE_MAX_SIZE_BYTES = 6 * 1024 * 1024;
export const JINGLE_MAX_DURATION_SEC = 240;

type DetectionMethod = BakedInDetectionMethod;

const newTextDetector = (): BakedInTextDetector => ({
  id: v4(),
  episodeId: null,
  episodeTitle: null,
  startMarker: '',
  startIncludedInAd: false,
  endMarker: '',
  endIncludedInAd: false,
});

const withDefaultTextDetector = (
  textDetectors: BakedInTextDetector[],
): BakedInTextDetector[] =>
  isEmpty(textDetectors) ? [newTextDetector()] : textDetectors;

const secondsToHMS = (totalSeconds: number) => {
  const safeSeconds = Math.max(0, totalSeconds || 0);
  return {
    h: Math.floor(safeSeconds / 3600),
    m: Math.floor((safeSeconds % 3600) / 60),
    s: Math.floor(safeSeconds % 60),
  };
};

const hmsToSeconds = (h: number, m: number, s: number) =>
  Math.max(0, h) * 3600 + Math.max(0, m) * 60 + Math.max(0, s);

// Returns the lowercased extension (no dot) when it's one this feature
// supports, otherwise null — callers treat null as a rejected format.
export const getJingleExtension = (filename: string): string | null => {
  const match = filename.toLowerCase().match(/\.([a-z0-9]+)$/);
  const extension = match?.[1] ?? null;
  return extension && JINGLE_ALLOWED_EXTENSIONS.includes(extension)
    ? extension
    : null;
};

// Format + size only need the File object, no decode — pure and sync so it's
// unit-testable without mocking the browser Audio API.
export const getJingleFileError = (file: {
  name: string;
  size: number;
}): string | null => {
  if (!getJingleExtension(file.name)) return 'Jingle file must be .mp3 or .wav.';
  if (file.size > JINGLE_MAX_SIZE_BYTES) return 'Jingle file must be 6MB or smaller.';
  return null;
};

// Duration must be decoded first (see getAudioDurationSec below); once known,
// bounds-checking it is pure and testable on its own. A non-finite duration
// (NaN/Infinity — some corrupt or unseekable files resolve metadata this way)
// is invalid, same as an out-of-range one.
export const getJingleDurationError = (durationSec: number): string | null =>
  !Number.isFinite(durationSec) ||
  durationSec <= 0 ||
  durationSec >= JINGLE_MAX_DURATION_SEC
    ? 'Jingle duration must be longer than 0 seconds and under 4 minutes.'
    : null;

const AUDIO_METADATA_TIMEOUT_MS = 10_000;

// Byte size alone can't tell clip length — decode the file into an audio
// element and read its metadata duration. Bounded by a timeout so a file
// whose metadata never resolves doesn't hang the upload flow forever.
const getAudioDurationSec = (file: File): Promise<number> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const audio = new Audio();
    let settled = false;

    const cleanup = () => {
      clearTimeout(timer);
      URL.revokeObjectURL(objectUrl);
    };

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('Timed out reading audio file metadata.'));
    }, AUDIO_METADATA_TIMEOUT_MS);

    audio.onloadedmetadata = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(audio.duration);
    };
    audio.onerror = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('Unable to read audio file metadata.'));
    };
    audio.src = objectUrl;
  });

const buildJingleUploadFormData = (fields: IUploadHeaders, file: File) => {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) =>
    formData.append(key, value),
  );
  formData.append('file', file);
  return formData;
};

// `detectionMethods` always holds all 4 keys (enforced by
// `ensureCollectionMoneFormat`/`ensureDetectionMethods`), so direct access is safe.
const getMethodEntry = <M extends DetectionMethod>(
  draft: BakedInAdvancedOptions,
  method: M,
) => draft.detectionMethods[method];

/**
 * Multiple detection methods can run at once — toggling one on/off only
 * touches that method's own fields, so an active sibling method never loses
 * its data.
 */
export const withDetectionMethodToggled = (
  draft: BakedInAdvancedOptions,
  method: DetectionMethod,
  enabled: boolean,
): BakedInAdvancedOptions => {
  if (enabled) {
    return {
      ...draft,
      detectionMethods: {
        ...draft.detectionMethods,
        [method]: { ...draft.detectionMethods[method], isEnabled: true },
      },
    };
  }

  switch (method) {
    case 'jingle':
      // Disabling jingle never deletes the uploaded file or its S3 object —
      // re-enabling must show the same file with no re-upload.
      return {
        ...draft,
        detectionMethods: {
          ...draft.detectionMethods,
          jingle: { ...draft.detectionMethods.jingle, isEnabled: false },
        },
      };
    case 'startAndEndPhrases':
      return {
        ...draft,
        detectionMethods: {
          ...draft.detectionMethods,
          startAndEndPhrases: {
            ...draft.detectionMethods.startAndEndPhrases,
            isEnabled: false,
            textDetectors: [newTextDetector()],
          },
        },
      };
    case 'adTranscriptMatch':
      return {
        ...draft,
        detectionMethods: {
          ...draft.detectionMethods,
          adTranscriptMatch: {
            ...draft.detectionMethods.adTranscriptMatch,
            isEnabled: false,
            fullText: '',
          },
        },
      };
    case 'aiPowered':
      return {
        ...draft,
        detectionMethods: {
          ...draft.detectionMethods,
          aiPowered: {
            ...draft.detectionMethods.aiPowered,
            isEnabled: false,
            freeTextSearch: { query: '', startTimeSec: 0, endTimeSec: 0 },
          },
        },
      };
    default:
      return draft;
  }
};

export const validate = (
  options: BakedInAdvancedOptions,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const startAndEndPhrases = getMethodEntry(options, 'startAndEndPhrases');
  const adTranscriptMatch = getMethodEntry(options, 'adTranscriptMatch');
  const aiPowered = getMethodEntry(options, 'aiPowered');

  // Only "Remove" requires a detection method — "Keep" allows saving with
  // none (the caller warns via a confirm dialog before that save proceeds).
  if (
    options.action === 'remove' &&
    !Object.values(options.detectionMethods).some((item) => item.isEnabled)
  ) {
    errors.detectionMethod = 'Select at least one detection method.';
    return errors;
  }

  if (startAndEndPhrases.isEnabled) {
    // A marker side is only required when its own "Included in ad" toggle is
    // on — that's the only side actually used for detection. A side left off
    // is optional and can stay empty.
    const hasInvalidDetector = startAndEndPhrases.textDetectors.some(
      (detector) =>
        (detector.startIncludedInAd && isEmpty(detector.startMarker.trim())) ||
        (detector.endIncludedInAd && isEmpty(detector.endMarker.trim())),
    );

    if (hasInvalidDetector) {
      errors.startAndEndPhrases =
        'Enter a marker for each side marked "Included in ad".';
    }
  }

  if (
    adTranscriptMatch.isEnabled &&
    isEmpty(adTranscriptMatch.fullText.trim())
  ) {
    errors.adTranscriptMatch = 'Paste the full ad transcript.';
  }

  if (aiPowered.isEnabled) {
    if (isEmpty(aiPowered.freeTextSearch.query.trim())) {
      errors.freeTextSearchQuery = 'Enter the search terms.';
    }

    const { startTimeSec, endTimeSec } = aiPowered.freeTextSearch;
    // The time frame is optional and defaults to 0/0 — only flag a range the
    // user actually set, and only when it's genuinely backwards (end < start;
    // an end time equal to start is a valid, untouched-looking window).
    const isUserSetTimeFrame = startTimeSec !== 0 || endTimeSec !== 0;
    if (isUserSetTimeFrame && endTimeSec < startTimeSec) {
      errors.freeTextSearchTime = 'End time must be after start time.';
    }
  }

  return errors;
};

// Save-error priority when several methods are invalid at once — matches the
// mockup's top-to-bottom method order.
const METHOD_ORDER: DetectionMethod[] = [
  'jingle',
  'startAndEndPhrases',
  'adTranscriptMatch',
  'aiPowered',
];

const METHOD_ERROR_KEYS: Record<DetectionMethod, string[]> = {
  jingle: ['jingle'],
  startAndEndPhrases: ['startAndEndPhrases'],
  adTranscriptMatch: ['adTranscriptMatch'],
  aiPowered: ['freeTextSearchQuery', 'freeTextSearchTime'],
};

export const BakedInAdvancedConfigModal = ({
  show,
  collectionId,
  config,
  isSaving,
  onCancel,
  onSave,
}: Props) => {
  const [draft, setDraft] = useState<BakedInAdvancedOptions>(config.advanced);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Gates the per-method error icon — it must stay hidden until the user has
  // tried to save at least once, not just whenever `errors` is non-empty.
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const [episodes, setEpisodes] = useState<ILibraryItem[]>([]);
  const [isLoadingEpisodes, toggleLoadingEpisodes] = useToggle(false);
  // Only one method's panel is expanded at a time — expanding another
  // collapses this one, but never unpicks it.
  const [openMethod, setOpenMethod] = useState<DetectionMethod | null>(null);
  // Id of the Start & End Phrases text detector whose episode picker overlay
  // is currently open, or null — at most one is ever open, same pattern as
  // openMethod above. Also drives that row's collapse wrapper to
  // overflow-visible so the overlay isn't clipped.
  const [openPickerId, setOpenPickerId] = useState<string | null>(null);
  const jingleFileInputRef = useRef<HTMLInputElement>(null);
  const methodRefs = useRef<Partial<Record<DetectionMethod, HTMLDivElement | null>>>(
    {},
  );

  useEffect(() => {
    if (!show) return;

    setDraft({
      ...config.advanced,
      detectionMethods: {
        ...config.advanced.detectionMethods,
        startAndEndPhrases: {
          ...config.advanced.detectionMethods.startAndEndPhrases,
          textDetectors: withDefaultTextDetector(
            config.advanced.detectionMethods.startAndEndPhrases.textDetectors,
          ),
        },
      },
    });
    setErrors({});
    setHasAttemptedSave(false);
    setOpenMethod(null);
  }, [show]);

  useEffect(() => {
    if (!show || isEmpty(collectionId)) return;

    fetchEpisodes();
  }, [show, collectionId]);

  const fetchEpisodes = async () => {
    try {
      toggleLoadingEpisodes(true);

      const response = await LibraryService.getLibraryItems({
        collection_id: [collectionId],
        page_size: 100,
        page_number: 1,
      });

      setEpisodes(response?.data?.library ?? []);
    } catch (err) {
      console.log(err);
    } finally {
      toggleLoadingEpisodes(false);
    }
  };

  const patchDraft = (patch: Partial<BakedInAdvancedOptions>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const patchMethodEntry = <M extends DetectionMethod>(
    method: M,
    patch: Partial<BakedInDetectionMethods[M]>,
  ) => {
    setDraft((prev) => ({
      ...prev,
      detectionMethods: {
        ...prev.detectionMethods,
        [method]: { ...prev.detectionMethods[method], ...patch },
      },
    }));
  };

  const setFieldError = (field: string, message?: string) => {
    setErrors((prev) => {
      if (!message) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return { ...prev, [field]: message };
    });
  };

  const handleToggleDetectionMethod = (
    method: DetectionMethod,
    enabled: boolean,
  ) => {
    setDraft((prev) => withDetectionMethodToggled(prev, method, enabled));
    setErrors({});
  };

  // Opening a method also picks it (first click = choose it); collapsing an
  // already-open method never unpicks it — only the radio does that.
  const handleToggleOpenMethod = (method: DetectionMethod) => {
    const isOpening = openMethod !== method;
    const nextOpenMethod = isOpening ? method : null;
    setOpenMethod(nextOpenMethod);

    if (isOpening && !getMethodEntry(draft, method).isEnabled) {
      handleToggleDetectionMethod(method, true);
    }

    // Collapsing/switching away from Start & End Phrases must close its
    // open picker too — the picker can't render outside its (now collapsed)
    // row.
    if (nextOpenMethod !== 'startAndEndPhrases') {
      setOpenPickerId(null);
    }
  };

  const handleUploadJingleClick = () => {
    jingleFileInputRef.current?.click();
  };

  const handleJingleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Allow re-selecting the same filename after a rejected/failed attempt.
    e.target.value = '';
    if (!file) return;

    const fileError = getJingleFileError(file);
    if (fileError) {
      setFieldError('jingle', fileError);
      return;
    }
    const extension = getJingleExtension(file.name) as string;

    let durationSec: number;
    try {
      durationSec = await getAudioDurationSec(file);
    } catch (err) {
      console.log(err);
      setFieldError('jingle', 'Could not read the jingle file.');
      return;
    }

    const durationError = getJingleDurationError(durationSec);
    if (durationError) {
      setFieldError('jingle', durationError);
      return;
    }

    setFieldError('jingle', undefined);

    try {
      const { data } = await UploadService.uploadJingle({
        collection_id: collectionId,
        extension,
      });

      const uploadResponse = await fetch(data.url, {
        method: 'POST',
        body: buildJingleUploadFormData(data.fields, file),
      });

      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed with status ${uploadResponse.status}`);
      }

      patchMethodEntry('jingle', {
        jingleFileName: file.name,
        jingleFileUrl: `${data.url}${data.fields.key}`,
      });
    } catch (err) {
      console.log(err);
      setFieldError('jingle', 'Failed to upload jingle file.');
    }
  };

  const handleAddTextDetector = () => {
    patchMethodEntry('startAndEndPhrases', {
      textDetectors: [
        ...getMethodEntry(draft, 'startAndEndPhrases').textDetectors,
        newTextDetector(),
      ],
    });
  };

  const handleChangeTextDetector = (
    id: string,
    patch: Partial<BakedInTextDetector>,
  ) => {
    patchMethodEntry('startAndEndPhrases', {
      textDetectors: getMethodEntry(
        draft,
        'startAndEndPhrases',
      ).textDetectors.map((detector) =>
        detector.id === id ? { ...detector, ...patch } : detector,
      ),
    });
  };

  const handleRemoveTextDetector = (id: string) => {
    const textDetectors = getMethodEntry(draft, 'startAndEndPhrases')
      .textDetectors;
    // Detector 1 is the permanent default row — at least one must remain.
    if (textDetectors.length <= 1) return;

    patchMethodEntry('startAndEndPhrases', {
      textDetectors: textDetectors.filter((detector) => detector.id !== id),
    });
  };

  const handleChangeFreeTextTime = (
    field: 'startTimeSec' | 'endTimeSec',
    part: 'h' | 'm' | 's',
    value: number,
  ) => {
    const freeTextSearch = getMethodEntry(draft, 'aiPowered').freeTextSearch;
    const current = secondsToHMS(freeTextSearch[field]);
    const next = { ...current, [part]: value };

    patchMethodEntry('aiPowered', {
      freeTextSearch: {
        ...freeTextSearch,
        [field]: hmsToSeconds(next.h, next.m, next.s),
      },
    });
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleSave = () => {
    if (isSaving) return;

    const nextErrors = validate(draft);
    setErrors(nextErrors);
    setHasAttemptedSave(true);

    if (!isEmpty(nextErrors)) {
      const firstErroredMethod = METHOD_ORDER.find((method) =>
        METHOD_ERROR_KEYS[method].some((key) => nextErrors[key]),
      );

      if (firstErroredMethod) {
        setOpenMethod(firstErroredMethod);
        if (firstErroredMethod !== 'startAndEndPhrases') {
          setOpenPickerId(null);
        }
        methodRefs.current[firstErroredMethod]?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }

      return;
    }

    onSave(draft);
  };

  const jingle = getMethodEntry(draft, 'jingle');
  const startAndEndPhrases = getMethodEntry(draft, 'startAndEndPhrases');
  const adTranscriptMatch = getMethodEntry(draft, 'adTranscriptMatch');
  const aiPowered = getMethodEntry(draft, 'aiPowered');

  const hasMethodError = (method: DetectionMethod) =>
    hasAttemptedSave &&
    METHOD_ERROR_KEYS[method].some((key) => Boolean(errors[key]));

  return (
    <div css={[customModalCss]}>
      <Modal
        classes="baked-in-advanced-modal"
        show={show}
        modalClosed={handleCancel}
      >
        <div tw="text-14">
          <div
            css={[sectionPaddingCss, dividerBottomCss]}
            tw="flex items-center justify-between"
          >
            <div tw="font-bold text-[2.5rem]">
              Baked-in advanced configuration
            </div>
            <button
              type="button"
              aria-label="Close"
              css={[closeButtonCss]}
              onClick={handleCancel}
            >
              ×
            </button>
          </div>

          <div>
            <section css={[sectionCss]}>
              <div tw="font-semibold mb-3">What to do with the baked-in ad</div>
              <ButtonGroup
                items={[
                  { label: 'Keep', value: 'keep' },
                  { label: 'Remove', value: 'remove' },
                ]}
                defaultValue={draft.action}
                onChange={(item) =>
                  patchDraft({ action: item.value as 'keep' | 'remove' })
                }
              />
            </section>

            <section css={[sectionCss]}>
              <div tw="font-semibold mb-3">Detection method</div>

              <DetectionMethodItem
                title="Jingle"
                isActive={jingle.isEnabled}
                isOpen={openMethod === 'jingle'}
                hasError={hasMethodError('jingle')}
                onToggleOpen={() => handleToggleOpenMethod('jingle')}
                onToggleActive={(enabled) =>
                  handleToggleDetectionMethod('jingle', enabled)
                }
                containerRef={(el) => (methodRefs.current.jingle = el)}
              >
                <div tw="flex items-center justify-between mb-4">
                  <span tw="text-13 text-sonnant-grey-6">Jingle file</span>
                  <div tw="flex items-center gap-x-3">
                    <span css={[chipCss]}>
                      {jingle.jingleFileName || 'no-file-selected'}
                    </span>
                    <input
                      ref={jingleFileInputRef}
                      type="file"
                      accept="audio/*"
                      hidden
                      onChange={handleJingleFileChange}
                    />
                    <button
                      type="button"
                      css={[outlineButtonCss]}
                      onClick={handleUploadJingleClick}
                    >
                      Upload jingle
                    </button>
                  </div>
                </div>
                <div tw="flex items-center justify-between">
                  <span tw="text-13 text-sonnant-grey-6">Detector</span>
                  <ButtonGroup
                    items={[
                      { label: 'Sonic', value: 'sonic' },
                      { label: 'Music', value: 'music' },
                    ]}
                    defaultValue={jingle.jingleDetector}
                    onChange={(item) =>
                      patchMethodEntry('jingle', {
                        jingleDetector: item.value as 'sonic' | 'music',
                      })
                    }
                  />
                </div>
                <ErrorMessage message={errors.jingle} />
              </DetectionMethodItem>

              <DetectionMethodItem
                title="Start & End Phrases"
                isActive={startAndEndPhrases.isEnabled}
                isOpen={openMethod === 'startAndEndPhrases'}
                hasError={hasMethodError('startAndEndPhrases')}
                onToggleOpen={() => handleToggleOpenMethod('startAndEndPhrases')}
                onToggleActive={(enabled) =>
                  handleToggleDetectionMethod('startAndEndPhrases', enabled)
                }
                overflowVisible={openPickerId !== null}
                containerRef={(el) =>
                  (methodRefs.current.startAndEndPhrases = el)
                }
              >
                {isLoadingEpisodes && (
                  <div tw="flex justify-center py-4">
                    <Loader />
                  </div>
                )}

                {startAndEndPhrases.textDetectors.map((detector, index) => (
                  <TextDetectorCard
                    key={detector.id}
                    index={index}
                    detector={detector}
                    episodes={episodes}
                    canRemove={startAndEndPhrases.textDetectors.length > 1}
                    onChange={(patch) =>
                      handleChangeTextDetector(detector.id, patch)
                    }
                    onRemove={() => handleRemoveTextDetector(detector.id)}
                    isPickerOpen={openPickerId === detector.id}
                    onTogglePicker={() =>
                      setOpenPickerId((prev) =>
                        prev === detector.id ? null : detector.id,
                      )
                    }
                  />
                ))}

                <button
                  type="button"
                  css={[dashedButtonCss, tw`w-full`]}
                  onClick={handleAddTextDetector}
                >
                  <span tw="text-16 leading-none">+</span> Add text detector
                </button>
                <ErrorMessage message={errors.startAndEndPhrases} />
              </DetectionMethodItem>

              <DetectionMethodItem
                title="Full Ad Transcript Match"
                isActive={adTranscriptMatch.isEnabled}
                isOpen={openMethod === 'adTranscriptMatch'}
                hasError={hasMethodError('adTranscriptMatch')}
                onToggleOpen={() => handleToggleOpenMethod('adTranscriptMatch')}
                onToggleActive={(enabled) =>
                  handleToggleDetectionMethod('adTranscriptMatch', enabled)
                }
                containerRef={(el) =>
                  (methodRefs.current.adTranscriptMatch = el)
                }
              >
                <div tw="text-13 text-sonnant-grey-6 mb-2">
                  Full ad transcript
                </div>
                <textarea
                  css={[textInputCss, tw`min-h-[8rem] resize-y`]}
                  placeholder="Paste the complete ad text here…"
                  value={adTranscriptMatch.fullText}
                  onChange={(e) =>
                    patchMethodEntry('adTranscriptMatch', {
                      fullText: e.target.value,
                    })
                  }
                />
                <ErrorMessage message={errors.adTranscriptMatch} />
              </DetectionMethodItem>

              <DetectionMethodItem
                title="AI-Powered Detection"
                isActive={aiPowered.isEnabled}
                isOpen={openMethod === 'aiPowered'}
                hasError={hasMethodError('aiPowered')}
                onToggleOpen={() => handleToggleOpenMethod('aiPowered')}
                onToggleActive={(enabled) =>
                  handleToggleDetectionMethod('aiPowered', enabled)
                }
                containerRef={(el) => (methodRefs.current.aiPowered = el)}
              >
                <div tw="text-13 text-sonnant-grey-6 mb-2">Search query</div>
                <input
                  type="text"
                  css={[textInputCss]}
                  placeholder="Enter search terms…"
                  value={aiPowered.freeTextSearch.query}
                  onChange={(e) =>
                    patchMethodEntry('aiPowered', {
                      freeTextSearch: {
                        ...aiPowered.freeTextSearch,
                        query: e.target.value,
                      },
                    })
                  }
                />
                <ErrorMessage message={errors.freeTextSearchQuery} />

                <hr tw="border-t[1px solid] border-sonnant-grey-light my-4" />

                <div tw="text-13 text-sonnant-grey-6 mb-3">
                  Time frame for search (optional)
                </div>
                <div tw="flex flex-wrap gap-x-12 gap-y-4">
                  <TimeFrameField
                    label="Start time"
                    seconds={aiPowered.freeTextSearch.startTimeSec}
                    onChangePart={(part, value) =>
                      handleChangeFreeTextTime('startTimeSec', part, value)
                    }
                  />
                  <TimeFrameField
                    label="End time"
                    seconds={aiPowered.freeTextSearch.endTimeSec}
                    onChangePart={(part, value) =>
                      handleChangeFreeTextTime('endTimeSec', part, value)
                    }
                  />
                </div>
                <ErrorMessage message={errors.freeTextSearchTime} />
              </DetectionMethodItem>

              <ErrorMessage message={errors.detectionMethod} />
            </section>

            <section css={[sectionCss]}>
              <div tw="font-semibold mb-3">
                Sponsor brands{' '}
                <span tw="font-normal text-sonnant-grey-6 text-12">
                  (for validation)
                </span>
              </div>
              <SponsorBrandsBox
                brands={draft.sponsorBrands}
                onChange={(sponsorBrands) => patchDraft({ sponsorBrands })}
              />
            </section>

            <ProgrammaticAdSection
              config={draft.programmaticAd}
              onChange={(programmaticAd) => patchDraft({ programmaticAd })}
            />
          </div>

          <div css={[sectionPaddingCss]} tw="flex justify-between">
            <button
              type="button"
              className="button btn-secondary large apply"
              tw="mb-0"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button btn-primary large cancel"
              tw="mb-0"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const ErrorMessage = ({ message }: { message?: string }) =>
  isEmpty(message) ? null : (
    <div tw="mt-2 text-12 text-sonnant-red">{message}</div>
  );

type DetectionMethodItemProps = {
  title: string;
  isActive: boolean;
  isOpen: boolean;
  // Shows a red error icon in the header, expanded or collapsed, once the
  // user has tried to save with this method invalid.
  hasError?: boolean;
  onToggleOpen: () => void;
  onToggleActive: (enabled: boolean) => void;
  containerRef?: (el: HTMLDivElement | null) => void;
  children: ReactNode;
  // Lets a descendant's absolutely-positioned overlay (e.g. the episode
  // picker) escape this row's collapse-animation clip. Only true while that
  // overlay is open — the row must revert to clipping once it's closed.
  overflowVisible?: boolean;
};

const DetectionMethodItem = ({
  title,
  isActive,
  isOpen,
  hasError,
  onToggleOpen,
  onToggleActive,
  containerRef,
  children,
  overflowVisible,
}: DetectionMethodItemProps) => {
  const isExpanded = isOpen;

  const handleToggleIndicator = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onToggleOpen();
  };

  // Header click only ever expands/collapses — it never unpicks. Opening a
  // not-yet-picked method also picks it (first click = choose it).
  const handleHeaderClick = () => {
    onToggleOpen();
  };

  // The checkbox is the only way to unpick — several methods can be checked
  // at once, independent of which one is currently expanded.
  const handleCheckboxClick = (e: MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    onToggleActive(!isActive);
  };

  return (
    <div
      ref={containerRef}
      tw="border[1px solid] rounded-lg mb-3 last:mb-0"
      css={[
        isActive ? tw`border-sonnant-purple-2` : tw`border-sonnant-grey-light`,
      ]}
    >
      <div
        tw="flex items-center gap-x-4 px-4 py-3 cursor-pointer select-none"
        css={[
          isActive && activeHeaderCss,
          isExpanded ? tw`rounded-t-lg` : tw`rounded-lg`,
          !isActive && tw`hover:bg-sonnant-grey-1`,
        ]}
        onClick={handleHeaderClick}
      >
        <span
          role="checkbox"
          aria-checked={isActive}
          aria-label={`${isActive ? 'Disable' : 'Enable'} ${title} detection`}
          tw="w-[1.8rem] h-[1.8rem] rounded-full border[2px solid] flex items-center justify-center flex-shrink-0 cursor-pointer"
          css={[
            isActive
              ? tw`border-sonnant-purple-2 bg-sonnant-purple-2`
              : tw`border-sonnant-grey-4`,
          ]}
          onClick={handleCheckboxClick}
        >
          {isActive && (
            <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
              <polyline
                points="1,4 4,7 9,1"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
        <span
          tw="flex-1 text-13 font-medium"
          css={[isActive && tw`text-sonnant-purple-2`]}
        >
          {title}
        </span>

        {hasError && (
          <ErrorIcon
            role="img"
            aria-label={`${title} has a validation error`}
            css={css`
              width: 1.6rem;
              height: 1.6rem;
              flex-shrink: 0;
              path {
                fill: #e7483d;
              }
            `}
          />
        )}

        {/* Expandable indicator — the mockup caret, flipped 180° when open */}
        <button
          type="button"
          aria-label={isExpanded ? `Collapse ${title}` : `Expand ${title}`}
          aria-expanded={isExpanded}
          css={[
            expandIndicatorCss,
            isExpanded && tw`transform[rotate(180deg)]`,
            !isActive && tw`opacity-40 pointer-events-none`,
          ]}
          onClick={handleToggleIndicator}
        >
          <span className="arrow-down" tw="m-0 top-0" />
        </button>
      </div>

      <div css={[expandableRowCss, isExpanded && expandedRowCss]}>
        <div css={[!overflowVisible && tw`overflow-hidden`]}>
          <div tw="px-4 py-4 border-t[1px solid] border-sonnant-grey-light">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

type TextDetectorCardProps = {
  index: number;
  detector: BakedInTextDetector;
  episodes: ILibraryItem[];
  canRemove: boolean;
  onChange: (patch: Partial<BakedInTextDetector>) => void;
  onRemove: () => void;
  // Controlled by the parent so only one card's picker is ever open at a
  // time (same single-open pattern as the method panels' openMethod).
  isPickerOpen: boolean;
  onTogglePicker: () => void;
};

const TextDetectorCard = ({
  index,
  detector,
  episodes,
  canRemove,
  onChange,
  onRemove,
  isPickerOpen,
  onTogglePicker,
}: TextDetectorCardProps) => {
  const [search, setSearch] = useState('');

  const filteredEpisodes = episodes.filter((episode) =>
    getLatestTitle(episode).toLowerCase().includes(search.toLowerCase()),
  );

  const handlePickEpisode = (episode: ILibraryItem) => {
    onChange({
      episodeId: episode.mediaid,
      episodeTitle: getLatestTitle(episode),
    });
    onTogglePicker();
    setSearch('');
  };

  return (
    <div tw="border[1px solid] border-sonnant-grey-light rounded-lg p-4 mb-3 relative bg-sonnant-grey-1">
      <div tw="flex items-center justify-between mb-3">
        <span tw="text-11 font-medium text-sonnant-purple-2 bg-sonnant-lavender rounded-full px-3 py-0.5">
          Detector {index + 1}
        </span>
        <div tw="flex items-center gap-x-3">
          <div tw="relative">
            <button
              type="button"
              css={[outlineButtonCss, tw`text-11!`]}
              onClick={onTogglePicker}
            >
              {detector.episodeId ? 'Change episode' : '+ Assign episode'}
            </button>

            {isPickerOpen && (
              <div tw="absolute z-[200] top-[calc(100% + 4px)] right-0 bg-white border[1px solid] border-sonnant-grey-light rounded-lg w-[28rem] shadow-lg">
                <div tw="p-2 border-b[1px solid] border-sonnant-grey-light">
                  <input
                    type="text"
                    css={[textInputCss, tw`mb-0`]}
                    placeholder="Search episodes…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div tw="max-h-[18rem] overflow-y-auto">
                  {filteredEpisodes.map((episode) => (
                    <div
                      key={episode.mediaid}
                      tw="px-3 py-2 text-13 break-words cursor-pointer border-b[1px solid] border-sonnant-grey-light hover:(bg-sonnant-grey-1 text-sonnant-purple-2)"
                      onClick={() => handlePickEpisode(episode)}
                    >
                      {getLatestTitle(episode)}
                    </div>
                  ))}
                  {isEmpty(filteredEpisodes) && (
                    <div tw="px-3 py-2 text-13 text-sonnant-grey-6">
                      No episodes found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {canRemove && (
            <span
              tw="text-16 leading-none text-sonnant-grey-5 cursor-pointer hover:text-sonnant-red"
              onClick={onRemove}
            >
              ×
            </span>
          )}
        </div>
      </div>

      {detector.episodeId && (
        <div tw="mb-3">
          <span css={[chipCss, tw`inline-flex items-center gap-x-2`]}>
            {detector.episodeTitle}
            <span
              tw="cursor-pointer text-sonnant-grey-5 hover:text-sonnant-red"
              onClick={() => onChange({ episodeId: null, episodeTitle: null })}
            >
              ×
            </span>
          </span>
        </div>
      )}

      <div tw="mb-3">
        <div tw="flex items-center justify-between mb-1">
          <span tw="text-12 text-sonnant-grey-6">Text detector for start</span>
          <div tw="flex items-center gap-x-2">
            <span tw="text-12 text-sonnant-grey-5">Included in ad</span>
            <Switcher
              isSmall
              checked={detector.startIncludedInAd}
              onCheckedChange={(startIncludedInAd) =>
                onChange({ startIncludedInAd })
              }
            />
          </div>
        </div>
        <input
          type="text"
          css={[textInputCss]}
          placeholder="Enter start marker text…"
          value={detector.startMarker}
          onChange={(e) => onChange({ startMarker: e.target.value })}
        />
      </div>

      <hr tw="border-t[1px solid] border-sonnant-grey-light my-3" />

      <div>
        <div tw="flex items-center justify-between mb-1">
          <span tw="text-12 text-sonnant-grey-6">Text detector for end</span>
          <div tw="flex items-center gap-x-2">
            <span tw="text-12 text-sonnant-grey-5">Included in ad</span>
            <Switcher
              isSmall
              checked={detector.endIncludedInAd}
              onCheckedChange={(endIncludedInAd) =>
                onChange({ endIncludedInAd })
              }
            />
          </div>
        </div>
        <input
          type="text"
          css={[textInputCss]}
          placeholder="Enter end marker text…"
          value={detector.endMarker}
          onChange={(e) => onChange({ endMarker: e.target.value })}
        />
      </div>
    </div>
  );
};

type StepperNumberInputProps = {
  value: number;
  min: number;
  max?: number;
  ariaLabel?: string;
  disabled?: boolean;
  onChange: (value: number) => void;
};

/**
 * Number field with always-visible increase/decrease buttons — the native
 * spinner only appears on hover and is unusable at this size.
 */
const StepperNumberInput = ({
  value,
  min,
  max,
  ariaLabel,
  disabled = false,
  onChange,
}: StepperNumberInputProps) => {
  const clamp = (next: number) => {
    if (isNaN(next)) return min;
    if (next < min) return min;
    if (typeof max === 'number' && next > max) return max;

    return next;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    if (isEmpty(raw)) {
      onChange(min);
      return;
    }

    onChange(clamp(parseInt(raw, 10)));
  };

  return (
    <div
      tw="inline-flex items-stretch bg-white border[1px solid] border-sonnant-grey-4 rounded overflow-hidden"
      css={[disabled && disabledCss]}
    >
      <input
        type="number"
        aria-label={ariaLabel}
        css={[stepperFieldCss]}
        value={value}
        min={min}
        max={max}
        step={1}
        disabled={disabled}
        onChange={handleChange}
      />
      <div tw="flex flex-col border-l[1px solid] border-sonnant-grey-4">
        <button
          type="button"
          aria-label={`Increase ${ariaLabel ?? 'value'}`}
          css={[stepperButtonCss]}
          tabIndex={-1}
          disabled={disabled}
          onClick={() => onChange(clamp(value + 1))}
        >
          ▲
        </button>
        <button
          type="button"
          aria-label={`Decrease ${ariaLabel ?? 'value'}`}
          css={[
            stepperButtonCss,
            tw`border-t[1px solid] border-sonnant-grey-4`,
          ]}
          tabIndex={-1}
          disabled={disabled}
          onClick={() => onChange(clamp(value - 1))}
        >
          ▼
        </button>
      </div>
    </div>
  );
};

type TimeFrameFieldProps = {
  label: string;
  seconds: number;
  onChangePart: (part: 'h' | 'm' | 's', value: number) => void;
};

const TimeFrameField = ({
  label,
  seconds,
  onChangePart,
}: TimeFrameFieldProps) => {
  const { h, m, s } = secondsToHMS(seconds);

  return (
    <div>
      <div tw="text-11 text-sonnant-grey-5 mb-2">{label}</div>
      <div tw="flex items-end gap-x-2">
        <div tw="flex flex-col items-center gap-y-1">
          <label tw="text-11 text-sonnant-grey-6 mb-0">h</label>
          <StepperNumberInput
            ariaLabel={`${label} hours`}
            value={h}
            min={0}
            onChange={(value) => onChangePart('h', value)}
          />
        </div>
        <span tw="text-15 font-medium text-sonnant-grey-5 pb-2">:</span>
        <div tw="flex flex-col items-center gap-y-1">
          <label tw="text-11 text-sonnant-grey-6 mb-0">m</label>
          <StepperNumberInput
            ariaLabel={`${label} minutes`}
            value={m}
            min={0}
            max={59}
            onChange={(value) => onChangePart('m', value)}
          />
        </div>
        <span tw="text-15 font-medium text-sonnant-grey-5 pb-2">:</span>
        <div tw="flex flex-col items-center gap-y-1">
          <label tw="text-11 text-sonnant-grey-6 mb-0">s</label>
          <StepperNumberInput
            ariaLabel={`${label} seconds`}
            value={s}
            min={0}
            max={59}
            onChange={(value) => onChangePart('s', value)}
          />
        </div>
      </div>
    </div>
  );
};

type SponsorBrandsBoxProps = {
  brands: string[];
  onChange: (brands: string[]) => void;
};

const SponsorBrandsBox = ({ brands, onChange }: SponsorBrandsBoxProps) => {
  const handleRemove = (index: number) => {
    onChange(brands.filter((_, i) => i !== index));
  };

  const handleAdd = (brand: string) => {
    const trimmed = brand.trim();
    if (isEmpty(trimmed)) return;
    if (brands.some((b) => b.toLowerCase() === trimmed.toLowerCase())) return;

    onChange([...brands, trimmed]);
  };

  return (
    <div tw="flex flex-wrap items-center gap-2 bg-white border[1px solid] border-sonnant-grey-4 rounded-lg px-3 py-2 min-h-[4.4rem]">
      {brands.map((brand, index) => (
        <span
          key={`${brand}-${index}`}
          css={[chipCss, tw`inline-flex items-center gap-x-2`]}
        >
          {brand}
          <span
            tw="cursor-pointer text-sonnant-grey-5 hover:text-sonnant-red"
            onClick={() => handleRemove(index)}
          >
            ×
          </span>
        </span>
      ))}

      {brands.length < MAX_SPONSOR_BRAND_COUNT && (
        <AddBrandChip onAdd={handleAdd} />
      )}
    </div>
  );
};

type AddBrandChipProps = {
  onAdd: (brand: string) => void;
};

const AddBrandChip = ({ onAdd }: AddBrandChipProps) => {
  const [isEditing, toggleEditing] = useToggle(false);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const commit = () => {
    onAdd(value);
    setValue('');
    toggleEditing(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
    if (e.key === 'Escape') {
      setValue('');
      toggleEditing(false);
    }
  };

  // The add field stays inside the brands box so chips keep wrapping around it.
  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        tw="w-[16rem] h-[2.6rem] px-3 py-0 mb-0 text-12 bg-white border[1px solid] border-sonnant-purple-2 rounded-full outline-none shadow-none"
        maxLength={MAX_SPONSOR_BRAND_LENGTH}
        placeholder="Brand name…"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commit}
      />
    );
  }

  return (
    <button
      type="button"
      css={[addBrandButtonCss]}
      onClick={() => toggleEditing(true)}
    >
      <span tw="text-14 leading-none">+</span> Add brand
    </button>
  );
};

type ProgrammaticAdSectionProps = {
  config: BakedInAdvancedOptions['programmaticAd'];
  onChange: (config: BakedInAdvancedOptions['programmaticAd']) => void;
};

const ProgrammaticAdSection = ({
  config,
  onChange,
}: ProgrammaticAdSectionProps) => (
  <section css={[sectionCss]}>
    <div tw="flex items-center justify-between mb-4">
      <div tw="font-semibold">Place programmatic ad</div>
      <Switcher
        hasLabel
        checked={config.isEnabled}
        onCheckedChange={(isEnabled) => onChange({ ...config, isEnabled })}
      />
    </div>

    <div
      tw="flex flex-col space-y-4 pl-3"
      css={[!config.isEnabled && disabledCss]}
    >
      <div tw="flex items-center justify-between">
        <span tw="text-13">Placement</span>
        <ButtonGroup
          items={[
            { label: 'Before', value: 'before' },
            { label: 'After', value: 'after' },
          ]}
          defaultValue={config.placement}
          onChange={(item) =>
            onChange({
              ...config,
              placement: item.value as 'before' | 'after',
            })
          }
        />
      </div>
      <div tw="flex items-center justify-between">
        <span tw="text-13">Roll type</span>
        <ButtonGroup
          items={[
            { label: 'Pre-roll', value: 'preroll' },
            { label: 'Mid-roll', value: 'midroll' },
            { label: 'Post-roll', value: 'postroll' },
          ]}
          defaultValue={config.rollType}
          onChange={(item) =>
            onChange({
              ...config,
              rollType: item.value as 'preroll' | 'midroll' | 'postroll',
            })
          }
        />
      </div>
      <div tw="flex items-center justify-between">
        <span tw="text-13">Ads per slot</span>
        <div tw="flex items-center gap-x-3">
          <StepperNumberInput
            ariaLabel="ads per slot"
            value={config.adsPerSlot}
            min={1}
            max={10}
            disabled={!config.isEnabled}
            onChange={(adsPerSlot) => onChange({ ...config, adsPerSlot })}
          />
          <span tw="text-12 text-sonnant-grey-5">(1–10)</span>
        </div>
      </div>
    </div>
  </section>
);

const customModalCss = css`
  .Modal.baked-in-advanced-modal {
    ${tw`(width[70rem] left[calc(50% - 35rem)] max-height[90vh] overflow-y-auto p-0)!`}

    /**
     * The 10px global scrollbar is laid out inside the modal, so the box
     * reflows the moment the content outgrows 90vh. Reserve the gutter up
     * front, and narrow \`.Modal\`'s \`transition: all\` down to the only
     * property it actually animates — otherwise every scroll-driven style
     * recalculation animates for 300ms and the modal drags behind the wheel.
     */
    scrollbar-gutter: stable;
    overscroll-behavior: contain;
    transition: top 300ms ease-out !important;

    /* The header row renders its own close button, flexed against the title. */
    > .customClose {
      display: none;
    }
  }
`;

const closeButtonCss = css`
  ${tw`inline-flex items-center justify-center w-[2.8rem] h-[2.8rem] p-0 m-0 text-20 leading-none border-none rounded cursor-pointer`}

  &,
  &:focus,
  &:active {
    ${tw`(bg-transparent text-sonnant-grey-6 shadow-none)!`}
  }

  &:hover {
    ${tw`(bg-sonnant-grey-1 text-sonnant-grey-6)!`}
  }
`;

/** The mockup separates every block with a single hairline rule. */
const dividerBottomCss = css`
  border-bottom: 1px solid rgb(229, 231, 235);
`;

/**
 * `.Modal` padding is zeroed below so the rules span the full modal width —
 * every block carries this identical inset instead.
 */
const sectionPaddingCss = css`
  ${tw`px-6 py-5`}
`;

const sectionCss = css`
  ${sectionPaddingCss}
  ${dividerBottomCss}
`;

const activeHeaderCss = css`
  background: rgba(85, 81, 255, 0.06);
`;

/**
 * Every button below re-asserts its colours on `:focus`/`:active` because
 * `App.scss` paints *any* focused or active <button> brand-purple with white
 * text, which otherwise hides the label as soon as the control is clicked.
 */
const expandIndicatorCss = css`
  ${tw`inline-flex items-center justify-center p-0 m-0 text-11 leading-none border-none cursor-pointer`}

  transition: transform 200ms ease-in-out;

  &,
  &:hover,
  &:focus,
  &:active {
    ${tw`(bg-transparent text-sonnant-grey-5 shadow-none)!`}
  }
`;

const chipCss = css`
  ${tw`bg-sonnant-grey-1 border[1px solid] border-sonnant-grey-light rounded-full px-3 py-0.5 text-12 text-sonnant-grey-6`}
`;

const textInputCss = css`
  ${tw`(w-full mb-0 px-3 py-2 text-13 bg-white border[1px solid] border-sonnant-grey-4 rounded shadow-none outline-none)!`}

  &:focus {
    ${tw`border-sonnant-purple-2!`}
  }
`;

const outlineButtonCss = css`
  ${tw`inline-flex items-center whitespace-nowrap px-3 py-1 m-0 text-12 border[1px solid] rounded cursor-pointer`}

  &,
  &:focus,
  &:active {
    ${tw`(text-sonnant-purple-2 bg-white border-sonnant-purple-2 shadow-none)!`}
  }

  &:hover {
    ${tw`(bg-sonnant-lavender text-sonnant-purple-2)!`}
  }
`;

const dashedButtonCss = css`
  ${tw`inline-flex items-center justify-center gap-x-2 px-3 py-2 m-0 text-12 border[1.5px dashed] rounded-lg cursor-pointer`}

  &,
  &:focus,
  &:active {
    ${tw`(text-sonnant-purple-2 bg-transparent border-sonnant-purple-2 shadow-none)!`}
  }

  &:hover {
    ${tw`(bg-sonnant-lavender text-sonnant-purple-2)!`}
  }
`;

const addBrandButtonCss = css`
  ${tw`inline-flex items-center gap-x-1 px-2 py-0.5 m-0 text-12 leading-none border-none rounded-full cursor-pointer`}

  &,
  &:focus,
  &:active {
    ${tw`(text-sonnant-purple-2 bg-transparent shadow-none)!`}
  }

  &:hover {
    ${tw`(bg-sonnant-lavender text-sonnant-purple-2)!`}
  }
`;

const stepperFieldCss = css`
  ${tw`(w-[4.6rem] h-[3.2rem] px-1 m-0 text-center text-13 bg-white border-none rounded-none shadow-none outline-none)!`}

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const stepperButtonCss = css`
  ${tw`flex items-center justify-center w-[2.2rem] h-[1.6rem] p-0 m-0 text-10 leading-none border-none cursor-pointer`}

  &,
  &:focus,
  &:active {
    ${tw`(bg-white text-sonnant-grey-5 shadow-none)!`}
  }

  &:hover:not(:disabled) {
    ${tw`(bg-sonnant-grey-1 text-sonnant-purple-2)!`}
  }

  &:disabled {
    ${tw`opacity-40 cursor-not-allowed`}
  }
`;

const disabledCss = css`
  ${tw`opacity-50 pointer-events-none`}
`;

const expandableRowCss = css`
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 280ms ease-in-out;
`;

const expandedRowCss = css`
  grid-template-rows: 1fr;
`;
